import router from '@/router'
import firebase from 'firebase/app';
import 'firebase/auth';
import format from 'date-fns/format'
import axios from 'axios'
import {
  getByUser,
  getCompany,
  buildSessionUser,
  decodeSessionUser,
  setSessionUserData,
  sanitizeSessionUser,
} from '../core/cacheService'
import { clearSessionLocalStorage } from './logoutStorage'
import { isAllowedConsoleUserType, requestLoginPin, verifyLoginPin } from './loginPin'
import { retryOnAuthPropagation } from './authRetry'

// Plan-tier gate. Paths listed here are locked on the 'free' plan tier,
// regardless of user_type/role. Keys MUST match the keys in the
// pagePermission object below (underscores, not dashes).
//
// Notes:
// - 'billing' is intentionally NOT here — free-tier users must reach
//   billing to request an upgrade.
// - 'insights' is intentionally NOT here either — the route is open to
//   free users, but only the 'dashboard' tab is accessible. Non-dashboard
//   tabs are gated inside the insights component itself.
const planPermission: { [path: string]: 'pro' } = {
  reports: 'pro',
  performance: 'pro',
  skills: 'pro',
  sparks: 'pro',
  integrations: 'pro',
  status_page: 'pro',
}

const AUTH_SESSION_ID_KEY = 'authSessionId'
const AUTH_LOGIN_ATTEMPT_ID_KEY = 'authLoginAttemptId'

const AUTH_LOG_EVENTS = new Set([
  'login_request',
  'pin_verify_success',
  'pin_verify_failed',
  'login_success',
  'login_failed',
  'logout',
  'logout_manual',
  'logout_idle_timeout',
  'logout_user_missing',
  'session_invalidated',
  'session_restore_success',
  'session_restore_failed',
  'ip_blocked',
  'sso_login_success',
  'sso_login_failed',
])

const createLogId = (prefix: string): string => {
  const random = Math.random().toString(36).slice(2, 12)
  return `${prefix}_${Date.now()}_${random}`
}

const getStoredLogId = (key: string, prefix: string): string => {
  try {
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const next = createLogId(prefix)
    localStorage.setItem(key, next)
    return next
  } catch (error) {
    return createLogId(prefix)
  }
}

const clearStoredLogId = (key: string) => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    // Ignore storage cleanup failures; logging must never block auth.
  }
}

const getLoginAttemptId = (): string =>
  getStoredLogId(AUTH_LOGIN_ATTEMPT_ID_KEY, 'attempt')

const getAuthSessionId = (): string =>
  getStoredLogId(AUTH_SESSION_ID_KEY, 'session')

const getCurrentRoute = (): string => {
  if (typeof window === 'undefined' || !window.location) return ''
  return `${window.location.pathname || ''}${window.location.search || ''}`
}

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch (error) {
    return ''
  }
}

const getUserAgent = (): string => {
  if (typeof navigator === 'undefined') return ''
  return navigator.userAgent || ''
}

const buildAuthLogPayload = (payload: any = {}) => {
  const now = new Date()
  return {
    createdate: payload.createdate || format(now, 'YYYY-MM-DD HH:mm:ss'),
    createdate_utc: payload.createdate_utc || now.toISOString(),
    log_type: 'auth',
    event: payload.event || payload.status,
    status: payload.status || payload.event,
    result: payload.result || '',
    reason: payload.reason || '',
    login_method: payload.login_method || 'pin',
    route: payload.route || getCurrentRoute(),
    user_agent: payload.user_agent || getUserAgent(),
    browser_timezone: payload.browser_timezone || getBrowserTimezone(),
    ...payload,
  }
}

const isAuthLogPayload = (object: any): boolean => {
  if (!object) return false
  return object.log_type === 'auth'
    || AUTH_LOG_EVENTS.has(object.event)
    || AUTH_LOG_EVENTS.has(object.status)
}

// Exported for UI consumers (e.g. PRO badges, billing page feature lists).
// These are normalized (underscore) keys; nav paths use hyphens, so callers
// must normalize before comparing.
export const PLAN_LOCKED_PATHS: ReadonlyArray<string> = Object.keys(planPermission)

export default {
    namespaced: true,
    state: {
      error: null,
      loading: false,
      user:null,
      userbeforelogin:null,
      activeIndex:'1',
      useremail:'',
      companyData: null,
      errorip:'',
      pagePermission: null,
      pinLoginInFlight: false,
    },
    mutations: {
      errorMessage (state:any, error:any) {
        state.error = error
      },
      loadingStatus(state:any, loading:boolean) {
        state.loading = loading
        if (!loading) {
          state.pinLoginInFlight = false
        }
      },
      pinLoginInFlightStatus(state:any, inFlight:boolean) {
        state.pinLoginInFlight = inFlight
      },
      setUserLogin (state:any, user:any) {
        // user.team='-L0Dg4P9voDNUxuEH8UV'
        // user.email='user@example.com'
        // user.user_type='leadership'
       // user.company='---TEST---'
       
        state.user = user
      },
      setActiveIndex(state:any, value:string){
        state.activeIndex = value
      },
      setUserEmail(state:any, value:string){
        state.useremail = value
      },
      setErrorIP(state:any, value:string){
        state.errorip = value
      },
      setUserBeforeLogin(state:any, user:any){
        state.userbeforelogin = user
      },
      setCompanyData(state:any, user:any){
        state.companyData = user
      },
      setPagePermission(state:any, data:any){
        state.pagePermission = data
      },
  
    },
    actions: {
      setUserLogin ({commit, dispatch}:any){
        const userData:any = localStorage.getItem('userData')

        if(userData){
          try {
            const parsed = decodeSessionUser(userData)
            commit('setUserLogin', parsed)
            setSessionUserData(parsed)
            dispatch('backfillAdviserFlag')
          } catch (error) {
            firebase.auth().signOut()
            clearSessionLocalStorage()
            commit('setUserLogin', null)
            router.replace('/login')
          }
        }
      },
      async validateCurrentUserExists({state, dispatch}:any){
        const user = state.user
        if (!user || !user.email) return true

        try {
          // Normalize like the login path does; a case-mismatched key reads
          // as an empty node and would log the user out as "missing".
          const emailKey = String(user.email).toLowerCase().trim().replace(/\./g, '||')
          const restoredUser = await firebase
          .database()
          .ref(`/users/${emailKey}`)
          .once('value')
          const hasUserRecord = restoredUser.val() && Object.keys(restoredUser.val()).length > 0
          if (hasUserRecord) return true
          const adviserSnapshot = await firebase
            .database()
            .ref(`/users_adviser/${emailKey}`)
            .once('value')
          const adviserRecord = adviserSnapshot.val()
          const hasAdviserRecord = adviserRecord && Object.keys(adviserRecord).length > 0
          if (hasAdviserRecord) return true

          // Adviser sessions are issued via verifyLoginPin/switchLoginAccount.
          // Client RTDB reads on /users_adviser are often denied even for valid sessions.
          if (user.is_adviser && firebase.auth().currentUser) {
            return true
          }

          await dispatch('authen/logout', { reason: 'user_missing' }, { root: true })
          return false
        } catch (error) {
          console.warn('[authen] active session validation failed:', error)
          return true
        }
      },
      // Tags pre-flag sessions with is_adviser. No-op once set.
      async backfillAdviserFlag({commit, state}:any){
        try {
          const user = state.user
          if (!user || !user.email) return
          if (user.is_adviser !== undefined) return

          const emailKey = user.email.replace(/\./g, '||')
          const adviserSnapshot = await firebase
            .database()
            .ref(`/users_adviser/${emailKey}`)
            .once('value')
          const isAdviser = !!adviserSnapshot.val()

          const updated = sanitizeSessionUser({ ...user, is_adviser: isAdviser })
          commit('setUserLogin', updated)
          setSessionUserData(updated)
        } catch (err) {
          console.warn('[authen] backfillAdviserFlag failed:', err)
        }
      },
      async loginpincode({commit, dispatch, state}:any, authData:any){
        if (state.pinLoginInFlight || state.loading) return
        commit('pinLoginInFlightStatus', true)
        commit('loadingStatus', true)
        const loginEmail = String(state.useremail || '').toLowerCase()
        const loginUser = state.userbeforelogin || {}
        const loginAttemptId = getLoginAttemptId()
        const loginLogBase = {
          attempt_id: loginAttemptId,
          user: loginEmail,
          company: loginUser.company || '',
          user_type: loginUser.user_type || '',
          is_adviser: !!loginUser.is_adviser,
          login_method: 'pin',
        }
        let pinVerification:any
        try {
          pinVerification = await verifyLoginPin(state.useremail, authData.pincode, {
            isAdviser: !!(state.userbeforelogin && state.userbeforelogin.is_adviser),
          })
        } catch (error) {
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'pin_verify_failed',
            status: 'pin_verify_failed',
            result: 'failed',
            reason: 'api_error',
          }), { root: true }).catch(() => {})
          dispatch('clearError')
          setTimeout(() => {
            commit('errorMessage', 'Verification is temporarily unavailable. Please try again in a few minutes.')
            commit('loadingStatus', false)
          }, 500)
          return
        }
        if(!pinVerification.verified){
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'pin_verify_failed',
            status: 'pin_verify_failed',
            result: 'failed',
            reason: 'invalid_or_expired_pin',
          }), { root: true }).catch(() => {})
          dispatch('clearError')
          setTimeout(() => {
            commit('errorMessage', pinVerification.message || 'Please recheck your PIN code')
            commit('loadingStatus', false)
          }, 500);
          return
        }

        if (!pinVerification.customToken) {
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'pin_verify_failed',
            status: 'pin_verify_failed',
            result: 'failed',
            reason: 'missing_custom_token',
          }), { root: true }).catch(() => {})
          dispatch('clearError')
          commit('errorMessage', 'Verification is temporarily unavailable. Please try again in a few minutes.')
          commit('loadingStatus', false)
          return
        }

        try {
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'pin_verify_success',
            status: 'pin_verify_success',
            result: 'success',
            user: (pinVerification.email || loginEmail || '').toLowerCase(),
            company: pinVerification.company || loginLogBase.company,
            user_type: pinVerification.user_type || loginLogBase.user_type,
            is_adviser: !!pinVerification.is_adviser,
          }), { root: true }).catch(() => {})
          await firebase.auth().signInWithCustomToken(pinVerification.customToken)
          const email = (pinVerification.email || state.useremail || '').toLowerCase()
          const emailKey = email.replace(/\./g, '||')
          let user:any = null
          let isAdviser = !!pinVerification.is_adviser

          if(email){
            try {
              user = await retryOnAuthPropagation(() => getByUser(email))
            } catch (error) {
              console.warn('[authen] getByUser failed during PIN login:', error)
            }
          }
          if(!user){
            try {
              const userAdviser = (await firebase.database().ref(`/users_adviser/${emailKey}`).once('value')).val()
              if(userAdviser){
                user = userAdviser
                isAdviser = true
              }
            } catch (error) {
              console.warn('[authen] users_adviser lookup failed during PIN login:', error)
            }
          }
          if (!user && (isAdviser || (loginUser && loginUser.is_adviser))) {
            const fallbackCompany = pinVerification.company || loginUser.company
            if (email && fallbackCompany) {
              user = {
                ...loginUser,
                email,
                company: fallbackCompany,
                user_type: pinVerification.user_type || loginUser.user_type || 'hr',
                is_adviser: true,
              }
              isAdviser = true
            }
          }
          if(!user){
            firebase.auth().signOut()
            dispatch('authen/addLogs', buildAuthLogPayload({
              ...loginLogBase,
              event: 'login_failed',
              status: 'login_failed',
              result: 'failed',
              reason: 'user_not_found',
              user: email,
              is_adviser: isAdviser,
            }), { root: true }).catch(() => {})
            dispatch('clearError')
            commit('errorMessage', 'Please recheck your user')
            setTimeout(() => {
              commit('loadingStatus', false)
            }, 500)
            return
          }

          // Advisers get a custom token scoped to their /users_adviser company
          // (pinVerification.company). getByUser() above may resolve a /users record
          // with a different company, which the hardened getCompany rejects (403).
          // Force the adviser's company to the token-scoped one so getCompany — and
          // the router guard that re-checks the stored company — both stay in scope.
          if (isAdviser && pinVerification.company) {
            user = { ...user, company: pinVerification.company }
          }
          const company = await retryOnAuthPropagation(() => getCompany(user.company))
          const user2 = buildSessionUser(user, company, isAdviser)
          commit('setUserLogin', user2)
          commit('setCompanyData', { ...company, key: user.company })

          localStorage.setItem('userVersion', await (await firebase.database().ref(`version/my_version`).once("value")).val())
          if (!setSessionUserData(user2)) {
            firebase.auth().signOut()
            dispatch('authen/addLogs', buildAuthLogPayload({
              ...loginLogBase,
              event: 'login_failed',
              status: 'login_failed',
              result: 'failed',
              reason: 'browser_storage_full',
              user: user.email,
              company: user.company,
              user_type: user.user_type,
              is_adviser: isAdviser,
            }), { root: true }).catch(() => {})
            dispatch('clearError')
            commit('errorMessage', 'Browser storage is full. Clear site data for this page and try again.')
            commit('loadingStatus', false)
            return
          }

          // Register account for multi-account switcher (for + email users)
          dispatch('accountSwitcher/registerAccount', {
            email: user.email,
            userData: user2,
            isSSO: false
          }, { root: true })

          // Clear the "adding account" flag if it was set
          localStorage.removeItem('addingAccount')

          const redirectTo = (user2 && user2.companyData && user2.companyData.onboarding_completed === false)
            ? '/setup-wizard'
            : (user.user_type == 'admin')
              ? '/members'
              : (user.user_type == 'developer')
                ? '/integrations'
                : '/'
          clearStoredLogId(AUTH_SESSION_ID_KEY)
          const sessionId = getAuthSessionId()

          const finishLogin = function() {
            setTimeout(() => {

              // Onboarding gate (precedence over user_type branches): if
              // companyData.onboarding_completed === false the new
              // self-serve signup landed but the wizard wasn't finished.
              // Everyone else — including legacy users with no field —
              // follows the existing user_type routing. The authData.freetrial
              // flag itself is left in place for call-site back-compat but
              // no longer influences routing.
              router.replace(redirectTo)
              commit('loadingStatus', false)
            }, 500);
          }

          /*save logs*/
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'login_success',
            status: 'login',
            result: 'success',
            reason: '',
            user: user.email,
            company: user.company,
            user_type: user.user_type,
            is_adviser: isAdviser,
            session_id: sessionId,
            redirect_to: redirectTo,
          }), {root:true}).then(() => {
            clearStoredLogId(AUTH_LOGIN_ATTEMPT_ID_KEY)
            finishLogin()
          }).catch(() => {
            clearStoredLogId(AUTH_LOGIN_ATTEMPT_ID_KEY)
            finishLogin()
          });
        } catch (error) {
          const err:any = error
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'login_failed',
            status: 'login_failed',
            result: 'failed',
            reason: 'sign_in_failed',
            error_message: err && err.message ? err.message : '',
          }), { root: true }).catch(() => {})
          dispatch('clearError')
          setTimeout(() => {
            commit('errorMessage', err && err.message ? err.message : 'Login failed. Please try again.')
            commit('loadingStatus', false)
          }, 500);
        }
      },
      async login ({commit, dispatch}:any, authData: {email: string; user?: any; company?: any; freetrial?: boolean}) {
        commit('loadingStatus', true)
        authData.email = authData.email.toLowerCase()
        const user = authData.user;
        const company = authData.company;
        const isAdviser = !!(authData as any).is_adviser || !!(user && user.is_adviser)
        const loginAttemptId = getLoginAttemptId()
        const loginLogBase = {
          attempt_id: loginAttemptId,
          user: authData.email,
          company: user && user.company ? user.company : '',
          user_type: user && user.user_type ? user.user_type : '',
          is_adviser: isAdviser,
          login_method: 'pin',
        }

        if(user) {
          if(company && company.ips){
            let ip = '';
            const url = 'https://api.ipify.org?format=json'
            await axios.get(url).then(response => {
              ip = response.data.ip
            })
            .catch(function (error) {
                console.log(error);
            });
            if(!company.ips.includes(ip)){
              dispatch('authen/addLogs', buildAuthLogPayload({
                ...loginLogBase,
                event: 'ip_blocked',
                status: 'ip_blocked',
                result: 'failed',
                reason: 'ip_not_allowed',
                blocked_ip: ip,
              }), { root: true }).catch(() => {})
              commit('setErrorIP',ip)
              commit('loadingStatus', false)
              return;
            }
          }
        }
       
        if(user && isAllowedConsoleUserType(user.user_type, isAdviser)){
          const pinRequest = await requestLoginPin(authData.email, { isAdviser })
          if (!pinRequest.ok) {
            dispatch('authen/addLogs', buildAuthLogPayload({
              ...loginLogBase,
              event: 'login_request',
              status: 'login_request',
              result: 'failed',
              reason: 'pin_request_failed',
            }), { root: true }).catch(() => {})
            dispatch('clearError')
            commit('errorMessage', pinRequest.message || 'Please recheck your login credentials.')
            commit('loadingStatus', false)
            return
          }
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'login_request',
            status: 'login_request',
            result: 'success',
            reason: '',
          }), { root: true }).catch(() => {})
          commit('setUserEmail',authData.email)
          commit('setUserBeforeLogin',{ ...user, is_adviser: isAdviser })
          commit('loadingStatus', false)
          localStorage.removeItem('userData')
          if(!authData.freetrial){
            router.replace('/pincode')
          }
         
        }else{
          dispatch('authen/addLogs', buildAuthLogPayload({
            ...loginLogBase,
            event: 'login_failed',
            status: 'login_failed',
            result: 'failed',
            reason: 'invalid_credentials_or_role',
          }), { root: true }).catch(() => {})
          setTimeout(() => {
            commit('loadingStatus', false)
            dispatch('clearError')
            commit('errorMessage', 'Please recheck your login credentials.')
          }, 500);
        }
      },
      async logout ({commit, dispatch}:any, payload:any = {}) {
        // Distinguish logout triggers in the audit trail; before this every
        // path logged a bare 'logout' and forced logouts were untraceable.
        const logoutReason = (payload && payload.reason) || ''
        const eventByReason: { [reason: string]: string } = {
          manual: 'logout_manual',
          idle_timeout: 'logout_idle_timeout',
          user_missing: 'logout_user_missing',
        }
        const logoutEvent = eventByReason[logoutReason] || 'logout'

        if(window.navigator && navigator.serviceWorker) {
          navigator.serviceWorker.getRegistrations()
          .then(function(registrations) {
            for(let registration of registrations) {
              registration.unregister();
            }
          })
          .catch(function(error) {
            console.warn('[authen] service worker cleanup failed:', error)
          });
        }
      
        const userData:any = localStorage.getItem('userData')
        let userEmail = ''
        let sessionUser:any = null
        try {
          sessionUser = decodeSessionUser(userData)
          userEmail = sessionUser && sessionUser.email ? sessionUser.email : ''
        } catch (error) {
          console.warn('[authen] failed to decode session user during logout:', error)
        }

        try {
          if (userEmail) {
            await dispatch('authen/addLogs', buildAuthLogPayload({
              event: logoutEvent,
              status: 'logout',
              result: 'success',
              reason: logoutReason,
              user: userEmail,
              company: sessionUser && sessionUser.company ? sessionUser.company : '',
              user_type: sessionUser && sessionUser.user_type ? sessionUser.user_type : '',
              is_adviser: !!(sessionUser && sessionUser.is_adviser),
              login_method: localStorage.getItem('sso') == 'Y' ? 'sso' : 'pin',
              session_id: localStorage.getItem(AUTH_SESSION_ID_KEY) || '',
            }), {root:true})
          }
        } catch (error) {
          console.warn('[authen] logout log failed:', error)
        }

        try {
          await firebase.auth().signOut()
        } catch (error) {
          console.warn('[authen] firebase signOut failed:', error)
        }

        // Notify account switcher before clearing storage so its Vuex state stays in sync.
        await dispatch('accountSwitcher/handleLogout', {
          email: userEmail,
          isAdviser: !!(sessionUser && sessionUser.is_adviser),
        }, { root: true })

        clearSessionLocalStorage(sessionUser)
        clearStoredLogId(AUTH_SESSION_ID_KEY)
        clearStoredLogId(AUTH_LOGIN_ATTEMPT_ID_KEY)
        commit('setUserLogin', null)

        if (router.currentRoute.path !== '/login') {
          router.replace('/login').catch(function(error:any) {
            if (!error || error.name === 'NavigationDuplicated') return
            console.warn('[authen] login redirect after logout failed:', error)
          })
        }
      },
      clearError ({commit}:any) {
        commit('errorMessage', null)
      },
      async getCompanyData ({commit, dispatch}:any, authData:any) {
        const company = await getCompany(authData.company)
        company.key=authData.company
        commit('setCompanyData', company)
      },
      pagePermission({commit, rootState}:any) {
        const pagePermission:any = {
          insights: {
            path: "insights",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          members: {
            path: "members",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          questions: {
            path: "questions",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          skills: {
            path: "skills",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          rewards: {
            path: "rewards",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          power_ups: {
            path: "power_ups",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          recognition: {
            path: "recognition",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          performance: {
            path: "performance",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          sparks: {
            path: "sparks",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          programs: {
            path: "programs",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          happily_village: {
            path: "happily_village",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          reports: {
            path: "reports",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: true,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          integrations: {
            path: "integrations",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: false,
              lock: false,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          billing: {
            path: "billing",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          settings: {
            path: "settings",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          awards: {
            path: "awards",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: true,
              lock: true,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: true,
              lock: true,
            },
            superadmin: {
              disable: true,
              lock: true,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          status_page: {
            path: "status-page",
            user: {
              disable: false,
              lock: false,
            },
            admin: {
              disable: false,
              lock: false,
            },
            hr: {
              disable: false,
              lock: false,
            },
            leadership: {
              disable: false,
              lock: false,
            },
            superleader: {
              disable: false,
              lock: false,
            },
            superadmin: {
              disable: false,
              lock: false,
            },
            developer: {
              disable: false,
              lock: false,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          setup_wizard: {
            path: "setup_wizard",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: true,
              lock: true,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: true,
              lock: true,
            },
            superadmin: {
              disable: true,
              lock: true,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
          document_developer: {
            path: "document_developer",
            user: {
              disable: true,
              lock: true,
            },
            admin: {
              disable: true,
              lock: true,
            },
            hr: {
              disable: true,
              lock: true,
            },
            leadership: {
              disable: true,
              lock: true,
            },
            superleader: {
              disable: true,
              lock: true,
            },
            superadmin: {
              disable: true,
              lock: true,
            },
            developer: {
              disable: true,
              lock: true,
            },
            ghost: {
              disable: false,
              lock: false,
            },
          },
        }

        // Free-tier plan gate: if the signed-in user's company is on the
        // 'free' plan, overwrite every role slot for premium paths so the
        // route is disabled + locked for everyone (plan wins over role).
        const planTier = rootState
          && rootState.authen
          && rootState.authen.user
          && rootState.authen.user.companyData
          && rootState.authen.user.companyData.plan_tier
        if (planTier === 'free') {
          Object.keys(planPermission).forEach((path:string) => {
            const entry = pagePermission[path]
            if (!entry) return
            Object.keys(entry).forEach((roleKey:string) => {
              if (roleKey === 'path') return
              entry[roleKey] = { disable: true, lock: true }
            })
          })
        }

        commit('setPagePermission', pagePermission);
      },
      async addLogs({ commit, rootState, dispatch }: any, object: any){
        const logObject = object || {}
        const isAuthLog = isAuthLogPayload(logObject)
        const sessionUser = rootState && rootState.authen ? rootState.authen.user : null
        let ip = logObject.ip || ''
        const now = new Date()

        if (!logObject.createdate) {
          logObject.createdate = format(now, 'YYYY-MM-DD HH:mm:ss')
        }

        if (!logObject.createdate_utc) {
          logObject.createdate_utc = now.toISOString()
        }

        if (isAuthLog) {
          logObject.log_type = logObject.log_type || 'auth'
          logObject.event = logObject.event || logObject.status
          logObject.status = logObject.status || logObject.event
          logObject.user = logObject.user || (sessionUser && sessionUser.email) || ''
          logObject.company = logObject.company || (sessionUser && sessionUser.company) || ''
          logObject.user_type = logObject.user_type || (sessionUser && sessionUser.user_type) || ''
          logObject.is_adviser = logObject.is_adviser !== undefined
            ? logObject.is_adviser
            : !!(sessionUser && sessionUser.is_adviser)
          logObject.login_method = logObject.login_method || (localStorage.getItem('sso') == 'Y' ? 'sso' : 'pin')
          logObject.result = logObject.result || ''
          logObject.reason = logObject.reason || ''
          logObject.route = logObject.route || getCurrentRoute()
          logObject.user_agent = logObject.user_agent || getUserAgent()
          logObject.browser_timezone = logObject.browser_timezone || getBrowserTimezone()
          logObject.session_id = logObject.session_id || localStorage.getItem(AUTH_SESSION_ID_KEY) || ''
        }

        if (!ip) {
          const url = 'https://api.ipify.org?format=json'
          await axios.get(url).then(response => {
            ip = response.data.ip
          })
          .catch(function (error) {
              console.log(error);
          });
        }
        if (isAuthLog) {
          logObject.location_source = logObject.location_source || 'ipify'
        }

        logObject.ip = ip;
        await firebase.firestore().collection('logs').add(logObject);
        return true;
      }
    },
    getters: {
        user : (state:any) => {
          return state.user
        },
        isAdvisor: (state:any) => {
          return !!(state.user && state.user.is_adviser)
        },
        error : (state:any) => {
            return state.error
        },
        loading: (state:any) => {
          return state.loading
        },
        checkLogin: (state:any) => {
          return (state.user)?true:false
        },
        activeIndex: (state:any) => {
          return state.activeIndex
        },
        useremail: (state:any) => {
          return state.useremail
        },
        companyData: (state:any) => {
          return state.companyData
        },
        errorip: (state:any) => {
          return state.errorip
        },
        pagePermission: (state:any) => {
          return state.pagePermission
        },
    }
  }

function dispatch(arg0: string) {
  throw new Error('Function not implemented.');
}
